import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { EvaluationForm } from "features/CenterContent/RoleContent/InternalMessage/shared/EvaluationForm";
import { InternalMessageContainer } from "features/CenterContent/RoleContent/InternalMessage/shared/InternalMessageContainer";
import { MessageList } from "features/CenterContent/RoleContent/InternalMessage/shared/MessageList";
import { FormTextareaHeader } from "features/CenterContent/RoleContent/ProductionDeviation/components/FormTextareaHeader";
import { DropsExpandableContent } from "features/CenterContent/VehicleDetails/shared/DropsExpandableContent";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { FC, useState } from "react";
import { FormProvider, useFormContext, UseFormReturn } from "react-hook-form";
import { FlexProps } from "shared/components/Spor";

type InternalMessageContentProps = {
  position: "static" | "absolute";
  evaluationForm?: EvaluationForm;
} & FlexProps;

type InternalMessageProps = InternalMessageContentProps & {
  formMethods: UseFormReturn<InternalMessagesForm>;
};

/**
 * Fills out the form of an internal message with related event (either Create/Edit).
 * It does not include any visibility, as it will copy the visibility from the event.
 *
 * Documentation: https://vygruppen.atlassian.net/wiki/spaces/TRAFFICGUI/pages/edit-v2/7957217376
 */
const InternalMessageContent: FC<InternalMessageContentProps> = ({
  position,
  evaluationForm,
  ...props
}) => {
  const { watch } = useFormContext<InternalMessagesForm>();

  const [active, setActive] = useState(false);
  const messageTextField = "internalMessages.0.message";

  const description = watch(messageTextField);
  if (description === undefined) {
    log(
      LogLevel.error,
      "InternalMessageContent",
      `FormField (${messageTextField}) does not have a defaultValue. This component is most likely set up wrong.\n
       Use internalMessagesFormDefault() or internalMessagesResponseToForm() as starting value.`,
    );
    return null;
  }

  const watchedFormField = watch("internalMessages");
  const messages = watchedFormField || [];
  const isEditMode = messages.length > 1;
  const isEdited = isEditMode || description !== "";

  return (
    <InternalMessageContainer position={position} {...props}>
      <FormTextareaHeader
        label="Intern melding"
        isEdited={isEdited}
        active={active}
        setActive={setActive}
      />
      <DropsExpandableContent collapsed={!active}>
        <MessageList maxHeight="205px" />
        {evaluationForm && <EvaluationForm {...evaluationForm} />}
      </DropsExpandableContent>
    </InternalMessageContainer>
  );
};

export const InternalMessage: FC<InternalMessageProps> = ({
  formMethods,
  ...props
}) => (
  <FormProvider {...formMethods}>
    <InternalMessageContent {...props} />
  </FormProvider>
);
